import { registerComponent, translate, useMatchLargeViewport, withProgressiveHydration } from '@avensia/excite';
import { forwardStyle, type ForwardStyleProps, styled } from '@glitz/react';
import * as React from 'react';
import { Block, desktopEdgeMarginDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import Image from 'webapp/components/image';
import { Carousel, useCarousel } from 'webapp/components/Carousel';
import { createMaxSmallViewportDecorator } from 'webapp/viewport';
import { sharedSpacing, white } from 'webapp/shared/sharedTheme';
import { CartIcon } from 'webapp/components/icons';
import { ResetButton } from 'webapp/components/button/button';
import {
  ShopTheLookDialogProvider,
  ShopTheLookDialog,
  useShopTheLookDialogContext,
} from 'webapp/components/shop-the-look-dialog';
import { theme } from 'nordicnest/shared/theme';
import { Theme } from 'webapp/theme';

export const ShopTheLookBlock = styled(
  forwardStyle((props: NordicNest.Api.ShopTheLookListApiModel & ForwardStyleProps) => {
    const isLargeViewport = useMatchLargeViewport();

    const carousel = useCarousel(props.id, {
      containScroll: 'keepSnaps',
      align: 'start',
      slidesToScroll: 'auto',
    });

    return (
      <ShopTheLookDialogProvider>
        <Base css={props.compose()}>
          <Title>{props.title}</Title>
          <Carousel
            gap={sharedSpacing.m}
            containerDecorator={containerDecorator}
            arrowNavigation={isLargeViewport}
            arrowPosition={theme.sharedSpacing.xl}
            arrowTheme={Theme.SecondaryCarouselButton}
            {...carousel}
          >
            {props.shopTheLookList.map((item, index) => (
              <ShopTheLookImage key={index} {...item} title={props.title} />
            ))}
          </Carousel>
        </Base>
        <ShopTheLookDialog />
      </ShopTheLookDialogProvider>
    );
  }),
);

export default registerComponent<NordicNest.Api.ShopTheLookListApiModel>(withProgressiveHydration(ShopTheLookBlock));

function ShopTheLookImage(props: NordicNest.Api.ShopTheLookApiModel & { title: string }) {
  const [hasMouseOver, setHasMouseOver] = React.useState<boolean>(false);
  const { setIsOpen, setSelectedItem, setTitle } = useShopTheLookDialogContext();

  const handleClick = React.useCallback(() => {
    setSelectedItem(props);
    setTitle(props.title);
    setIsOpen(true);
  }, [props, setIsOpen, setSelectedItem, setTitle]);

  return (
    <ImageButton
      onClick={handleClick}
      onMouseEnter={() => setHasMouseOver(true)}
      onMouseLeave={() => setHasMouseOver(false)}
      aria-label={translate(t => t.productPage.shopTheLookImageButtonText)}
    >
      <ItemImage
        image={{
          url: props.images[0].url,
          alt: props.images[0].altText,
          width: props.images[0].width || 600,
          height: props.images[0].height || 600,
        }}
      />
      <ProductCount css={hasMouseOver && showAnimationDecorator}>
        <StyledCartIcon />
        {props.skus.length}
      </ProductCount>
    </ImageButton>
  );
}

const Base = styled(
  Block,
  maxContentNormalWidthDecorator,
  desktopEdgeMarginDecorator,
  createMaxSmallViewportDecorator({ marginBottom: t => t.sharedSpacing.xl }),
  {
    marginBottom: t => t.sharedSpacing.xxl,
  },
);

const Title = styled.h2(
  createMaxSmallViewportDecorator({
    margin: { x: t => t.sharedSpacing.m },
  }),
  {
    fontWeight: t => t.fontWeight?.bold,
    fontSize: t => t.sharedFontSize.xl,
    marginBottom: t => t.sharedSpacing.xl,
    lineHeight: 'normal',
  },
);

const ItemImage = styled(
  Image,
  createMaxSmallViewportDecorator({
    height: t => `calc(100vw - ${t.sharedSpacing.xl}px - ${t.sharedSpacing.m}px)`,
    width: 'auto',
  }),
  {
    height: 600,
    maxHeight: 600,
    width: 'auto',
    objectFit: 'cover',
  },
);

const ImageButton = styled(ResetButton, {
  position: 'relative',
  flexShrink: 0,
});

const ProductCount = styled.div(
  createMaxSmallViewportDecorator({
    height: 24,
    fontSize: t => t.sharedFontSize.xxs,
    padding: { x: t => t.sharedSpacing.xs, y: t => t.sharedSpacing.xxs },
    gap: 7,
    opacity: 1,
  }),
  {
    position: 'absolute',
    bottom: t => t.sharedSpacing.m,
    left: t => t.sharedSpacing.m,
    backgroundColor: white,
    height: 32,
    borderRadius: 50,
    padding: { x: t => t.sharedSpacing.m, y: t => t.sharedSpacing.xs },
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    fontSize: t => t.sharedFontSize.xs,
    opacity: 0,
    transition: {
      property: 'opacity',
      timingFunction: 'ease-in-out',
      duration: 400,
    },
  },
);

const showAnimationDecorator = styled({
  opacity: 1,
});

const StyledCartIcon = styled(CartIcon, {
  fontSize: t => t.sharedFontSize.xxxs,
});

const containerDecorator = styled(
  createMaxSmallViewportDecorator({
    margin: { x: t => t.sharedSpacing.m },
  }),
  {
    display: 'flex',
  },
);
